import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { useThemeColor } from "@/hooks/useThemeColor";

import { useFirebase } from "@components/firebase/useFirebase";
import { getAllAnchors } from "@/services/firebase/anchor-functions";
import { AnchorModel, DateParser } from "common247";
import { AnchorCard } from "@/components/anchors/AnchorCard";
import { formatFavouriteRatingGroup } from "@/components/media/MediaGallery";

const ANCHOR_SORT_OPTIONS: ISortOption<AnchorModel>[] = [
  { key: "name", label: "Name", getValue: (a) => a.name || "" },
  { key: "type", label: "Type", getValue: (a) => a.type || "" },
  { key: "favouriteRating", label: "Favourite Rating", getValue: (a) => a.favouriteRating ?? 3 },
  { key: "createdAt", label: "Created Date", getValue: (a) => a.createdAt || 0 },
  { key: "address", label: "Address / Location", getValue: (a) => a.address || a.location?.text || "" },
  { key: "owner", label: "Owner", getValue: (a) => a.ownerDisplayName || "" },
];

const ANCHOR_GROUP_OPTIONS: IGroupOption<AnchorModel>[] = [
  {
    key: "type",
    label: "Type",
    getGroupValue: (a) => (a.type ? a.type.charAt(0).toUpperCase() + a.type.slice(1) : "Other"),
  },
  {
    key: "favouriteRating",
    label: "Favourite Rating",
    getGroupValue: (a) => formatFavouriteRatingGroup(a.favouriteRating),
  },
  {
    key: "owner",
    label: "Owner",
    getGroupValue: (a) => a.ownerDisplayName || "Unknown Owner",
  },
  {
    key: "year",
    label: "Year",
    getGroupValue: (a) => {
      if (a.date) {
        const parsed = DateParser.parse(a.date);
        if (parsed.from?.year) return String(parsed.from.year);
      }
      return a.createdAt ? String(new Date(a.createdAt).getFullYear()) : "No Date";
    },
  },
  {
    key: "address",
    label: "Address",
    getGroupValue: (a) => a.address || "No Address Specified",
  },
];

/**
 * Administrative screen displaying all platform anchors with view, sort, group, and search capabilities.
 */
export default function AnchorAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [anchors, setAnchors] = useState<AnchorModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(
    async (isRefresh = false) => {
      if (!services) return;
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await getAllAnchors(services, {});
        if (response.status) {
          setAnchors(response.anchors || []);
        } else {
          CustomAlert.alert("Error", response.error || "Failed to fetch anchors.");
        }
      } catch (error) {
        console.error("Error fetching anchors:", error);
        CustomAlert.alert("Error", "An unexpected error occurred while fetching anchors.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [services]
  );

  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  return (
    <ThemedScreen title="All Anchors" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        <ThemedText type="stamp" style={{ color: textMuted, marginBottom: 6 }}>
          {`${anchors.length} TOTAL ${anchors.length === 1 ? "ANCHOR" : "ANCHORS"}`}
        </ThemedText>

        <ThemedList<AnchorModel>
          data={anchors}
          sortOptions={ANCHOR_SORT_OPTIONS}
          defaultSortKey="createdAt"
          defaultSortDirection="desc"
          groupOptions={ANCHOR_GROUP_OPTIONS}
          defaultGroupKey="owner"
          persistenceKey="admin-all-anchors"
          pageSize={24}
          filterKeys={["name", "type", "description", "address", "ownerDisplayName"]}
          filterPlaceholder="Search all anchors..."
          loading={loading}
          onRefresh={() => fetchData(true)}
          refreshing={refreshing}
          emptyText="No anchors found in the system."
          contentContainerStyle={styles.listContent}
          renderItem={(item, _index, _isHovered, viewMode) => (
            <AnchorCard
              anchor={item}
              showOwner={true}
              isAdminView={true}
              canEditTags={false}
              viewMode={viewMode}
              onPress={() => {}}
            />
          )}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 8,
    minHeight: 0,
  },
  listContent: {
    paddingBottom: 24,
    gap: 12,
  },
});
